import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import { ResourceContentRenderer } from "@/components/portal/render/resource-content-renderer";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { extractPortalErrorCode, portalRpc } from "@/lib/portal-rpc";
import { PortalLayout } from "./portal-layout";
import { PortalShell, usePortalContext } from "./portal-shell";

function ContentInner({ resourceId }: { resourceId: string }) {
  const { readOnly } = usePortalContext();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const queryOpts = portalRpc.getContent.queryOptions({ input: { resourceId } });
  const content = useQuery(queryOpts);
  const contentKey = queryOpts.queryKey;

  const sidebar = useQuery({ ...portalRpc.getSidebar.queryOptions(), staleTime: Infinity });
  const isNotionMirror = useMemo(() => {
    if (!sidebar.data) return false;
    return sidebar.data.sections.some((s) =>
      s.resources.some((r) => r.resourceId === resourceId && r.renderType === "notion_mirror"),
    );
  }, [sidebar.data, resourceId]);

  // SPEC-NOT-009: fire trackResourceOpen exactly once on mount.
  const trackOpen = useMutation({
    ...portalRpc.trackResourceOpen.mutationOptions(),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire-once on mount per resource.
  useEffect(() => {
    trackOpen.mutate({ resourceId });
  }, [resourceId]);

  const toggleStep = useMutation({
    ...portalRpc.toggleProcessStep.mutationOptions(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: contentKey }),
  });

  const toggleTool = useMutation({
    ...portalRpc.toggleToolSetup.mutationOptions(),
    onMutate: async ({ toolSetupId }) => {
      await queryClient.cancelQueries({ queryKey: contentKey });
      const prev = queryClient.getQueryData<typeof content.data>(contentKey);
      if (prev?.tools) {
        queryClient.setQueryData(contentKey, {
          ...prev,
          tools: prev.tools.map((t) =>
            t.toolSetupId === toolSetupId
              ? {
                  ...t,
                  state: (t.state === "configured" ? "pending" : "configured") as
                    | "pending"
                    | "configured",
                }
              : t,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctxPrev) => {
      if (ctxPrev?.prev) queryClient.setQueryData(contentKey, ctxPrev.prev);
      toast({ title: "Não foi possível atualizar.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: contentKey });
      queryClient.invalidateQueries({ queryKey: portalRpc.getToday.queryOptions().queryKey });
    },
  });

  // Inline-tools variant: get-content emits synthetic ids `${resourceId}:${idx}`
  // for tools stored in the resource JSONB (no onboarding_tool_setups row). These
  // persist by tool index via toggleResourceTool instead of toggleToolSetup.
  const toggleResourceTool = useMutation({
    ...portalRpc.toggleResourceTool.mutationOptions(),
    onMutate: async ({ toolIndex }) => {
      await queryClient.cancelQueries({ queryKey: contentKey });
      const prev = queryClient.getQueryData<typeof content.data>(contentKey);
      const syntheticId = `${resourceId}:${toolIndex}`;
      if (prev?.tools) {
        queryClient.setQueryData(contentKey, {
          ...prev,
          tools: prev.tools.map((t) =>
            t.toolSetupId === syntheticId
              ? {
                  ...t,
                  state: (t.state === "configured" ? "pending" : "configured") as
                    | "pending"
                    | "configured",
                }
              : t,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctxPrev) => {
      if (ctxPrev?.prev) queryClient.setQueryData(contentKey, ctxPrev.prev);
      toast({ title: "Não foi possível atualizar.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: contentKey });
      queryClient.invalidateQueries({ queryKey: portalRpc.getToday.queryOptions().queryKey });
    },
  });

  const refreshNotion = useMutation({
    ...portalRpc.refreshNotionContent.mutationOptions(),
    onSuccess: (fresh) => {
      queryClient.setQueryData(contentKey, fresh);
      toast({ title: "Conteúdo atualizado a partir do Notion." });
    },
    onError: () =>
      toast({ title: "Não foi possível sincronizar com o Notion.", variant: "destructive" }),
  });

  const [askingId, setAskingId] = useState<string | null>(null);
  const askOwner = useMutation({
    ...portalRpc.askToolOwner.mutationOptions(),
    onMutate: ({ toolSetupId }) => setAskingId(toolSetupId),
    onSuccess: (data) => {
      if (data.ok) {
        toast({ title: `DM enviada no Slack para ${data.sentTo}.` });
      } else if (data.error === "owner_unreachable") {
        toast({
          title: "Não foi possível contatar o responsável da ferramenta",
          description: `Envie um e-mail para ${data.ownerEmail}.`,
        });
      }
    },
    onError: () => toast({ title: "Não foi possível enviar a mensagem.", variant: "destructive" }),
    onSettled: () => {
      setAskingId(null);
      queryClient.invalidateQueries({ queryKey: contentKey });
    },
  });

  if (content.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        {isNotionMirror && (
          <p className="text-sm text-muted-foreground">
            Carregando conteúdo sincronizado com o Notion. Isso pode levar alguns instantes.
          </p>
        )}
      </div>
    );
  }

  if (content.error) {
    const code = extractPortalErrorCode(content.error);
    if (code === "resource_not_found") {
      return (
        <Card className="p-8 text-center space-y-1">
          <p className="text-sm font-medium">Recurso indisponível</p>
          <p className="text-xs text-muted-foreground">
            Pode ter sido removido ou não está visível para seu onboarding.
          </p>
        </Card>
      );
    }
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Não foi possível carregar este conteúdo.</p>
      </Card>
    );
  }

  const data = content.data!;

  const firstBlock = data.contentBlocks[0] as Record<string, unknown> | undefined;
  const heroDuplicatesTitle =
    firstBlock?.type === "hero" &&
    [firstBlock.headline, firstBlock.title]
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .includes(data.title.trim());

  return (
    <div className="space-y-6">
      {!heroDuplicatesTitle && (
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
          {readOnly && (
            <div className="text-xs text-muted-foreground mt-2">
              Somente leitura · onboarding finalizado
            </div>
          )}
        </header>
      )}
      <ResourceContentRenderer
        data={data}
        readOnly={readOnly}
        onToggleStep={(stepIndex, checked) => toggleStep.mutate({ resourceId, stepIndex, checked })}
        onToggleTool={(toolSetupId) => {
          // Synthetic id `${resourceId}:${idx}` → inline-tools resource; UUID → real setup.
          const sep = toolSetupId.lastIndexOf(":");
          const toolIndex = sep === -1 ? Number.NaN : Number(toolSetupId.slice(sep + 1));
          if (sep !== -1 && Number.isInteger(toolIndex)) {
            toggleResourceTool.mutate({ resourceId: toolSetupId.slice(0, sep), toolIndex });
          } else {
            toggleTool.mutate({ toolSetupId });
          }
        }}
        onAskOwner={(toolSetupId) => askOwner.mutate({ toolSetupId })}
        askingId={askingId}
        onRefreshNotion={() => refreshNotion.mutate({ resourceId })}
        isRefreshingNotion={refreshNotion.isPending}
      />
    </div>
  );
}

export default function PortalContent() {
  const [, params] = useRoute<{ resourceId: string }>("/onboarding/content/:resourceId");
  const resourceId = params?.resourceId ?? "";
  return (
    <PortalShell>
      <PortalLayout>
        {resourceId ? (
          <ContentInner resourceId={resourceId} />
        ) : (
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Recurso inválido.</p>
          </Card>
        )}
      </PortalLayout>
    </PortalShell>
  );
}
